import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import CodeIcon from '@mui/icons-material/Code';
import ArticleIcon from '@mui/icons-material/Article';
import LinkIcon from '@mui/icons-material/Link';

/* ─── Static nav items for multi-page routing ─── */
const navItems = [
  { label: 'Dashboard', path: '/', icon: <HomeIcon sx={{ fontSize: 18 }} /> },
  { label: 'Projects', path: '/projects', icon: <CodeIcon sx={{ fontSize: 18 }} /> },
  { label: 'Blog', path: '/blogs', icon: <ArticleIcon sx={{ fontSize: 18 }} /> },
  { label: 'Connect', path: '/links', icon: <LinkIcon sx={{ fontSize: 18 }} /> },
];

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path: string) => location.pathname === path;

  const drawer = (
    <div onClick={handleDrawerToggle} style={{ textAlign: 'center' }}>
      <Typography
        variant="h6"
        sx={{ my: 2, color: 'primary.main', fontWeight: 800, letterSpacing: '-0.02em' }}
      >
        @shvms
      </Typography>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.label}
            component={RouterLink}
            to={item.path}
            sx={{
              textDecoration: 'none',
              justifyContent: 'center',
            }}
          >
            <ListItemText
              primary={item.label}
              sx={{
                textAlign: 'center',
                '& .MuiListItemText-primary': {
                  color: isActive(item.path) ? 'primary.main' : 'text.primary',
                  fontWeight: isActive(item.path) ? 700 : 400,
                  '&:hover': {
                    color: 'primary.main',
                  },
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: 'rgba(10, 10, 10, 0.75)',
          backdropFilter: 'blur(16px)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.4)',
          zIndex: theme.zIndex.appBar + 1,
        }}
      >
        <Toolbar sx={{ maxWidth: '75rem', margin: '0 auto', width: '100%' }}>
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: 1,
              color: 'primary.main',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              textDecoration: 'none',
            }}
          >
            @shvms
          </Typography>

          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    color: isActive(item.path) ? 'primary.main' : 'text.primary',
                    fontWeight: isActive(item.path) ? 700 : 500,
                    position: 'relative',
                    '&::after': isActive(item.path)
                      ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 24,
                          height: 2,
                          borderRadius: 1,
                          backgroundColor: 'primary.main',
                        }
                      : {},
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'rgba(239, 196, 48, 0.06)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          )}

          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ ml: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 240,
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;